import { useStore } from '../js/store';
import AppService, { Utilities } from "s2s-app-service";
import useContacts from './contacts';
import { extractContactNameFromContactArray, processServiceErrorsToAlerts } from '../js/utilities';
import ObjectMerge from 'object-merge';
import useAlerts from '../hooks/alerts';

const initialState = {
  lastNumber: "",
  activeCallId: "",
  incomingCalls: {},
  inprogressCalls: {
  },
  voicemailCount: 0,
  dialPlanOptions: {},
  isMerging: false,
  mergedCalls: {}
};

const useCallInfo = () => {
  const [state, setState] = useStore();
  const [, , , { getContactDataByPhoneNumber }] = useContacts();
  const [, { createErrorAlert }] = useAlerts();

  const removeAudioTag = (tagId) => {
    const audioElement = document.getElementById('audio-' + tagId);

    if (audioElement) {
      audioElement.pause();
      audioElement.parentNode.removeChild(audioElement);
    }
  };

  const playTone = (id, src, sinkId) => {
    const newAudio = new Audio(src);
    Object.assign(newAudio, { id: `audio-${id}`, loop: true });
    document.body.appendChild(newAudio);

    if (sinkId) newAudio.setSinkId(sinkId).catch((e) => console.warn('set sink id error', e));

    newAudio.play().catch((e) => console.warn('tone play error', e));
  };

  const setActiveCallId = (id) => {

    return setState((old) => {
      // add call to incoming calls
      const stateCopy = { ...state };

      if (typeof stateCopy.callInfo.inprogressCalls[id] !== "undefined") {
        stateCopy.callInfo.activeCallId = id;
        // console.log('ssssssssss', id);
      } else {
        console.error('id is not a inprogress call', id);
      }
      return stateCopy;
    });
  };

  const saveAudioSettings = (audioSettings) => {
    return setState((old) => {
      let stateCopy = ObjectMerge({}, old);

      // if audioSettings is not passed in, use the current settings and set volume to 1.
      // this is to handle resetting the audio for the call in progress when answered.
      if (audioSettings === undefined) {
        audioSettings = {};
        audioSettings.inputDevice = {
          id: "",
          kind: 'audio_in',
          label: undefined
        };
        audioSettings.outputDevice = {
          id: stateCopy.settings.selectedSpeaker.id,
          kind: 'audio_out',
          label: stateCopy.settings.selectedSpeaker.label
        };
        audioSettings.outputVolume = 1;

        // do i have an active call
        if (typeof stateCopy.callInfo.activeCallId !== "undefined" && typeof stateCopy.callInfo.inprogressCalls[stateCopy.callInfo.activeCallId] !== "undefined") {
          // ok have an active call
          if (typeof stateCopy.callInfo.inprogressCalls[stateCopy.callInfo.activeCallId].audioSettings !== "undefined") {
            // ok got active call with already set audio settings so i am just going to use it
            audioSettings = { ...stateCopy.callInfo.inprogressCalls[stateCopy.callInfo.activeCallId]['audioSettings'] };
          }
        } else {
          // Use the data passed in for audio settings
          // this was due to end user changing settings

          // look up the corresponding id for selected input and output devices from
          // the settings store
          const inputDeviceId = stateCopy.settings.audioDevices.reduce((p, c) => {
            if (p && p.length > 0) return p;
            if (c.label === audioSettings.inputDevice) return c.id;
            return p;
          }, "");

          const outputDeviceId = stateCopy.settings.speakerDevices.reduce((p, c) => {
            if (p && p.length > 0) return p;
            if (c.label === audioSettings.outputDevice) return c.id;
            return p;
          }, "");

          // Augment data so that input and output devices are objects that are
          // expected by Verto
          audioSettings.inputDevice = {
            id: inputDeviceId,
            kind: 'audio_in',
            label: audioSettings.inputDevice
          };
          audioSettings.outputDevice = {
            id: outputDeviceId,
            kind: 'audio_out', label: audioSettings.outputDevice
          };
        }

        try {
          // use callService setAudio method 
          AppService.getInstance().getCallService().setAudio(stateCopy.callInfo.activeCallId, audioSettings);
        } catch (e) {
          const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

          createErrorAlert({
            message,
            details: [alertBody],
            autoCloseTimeout: 10,
          });
        }

        // see if activeCallId is  inprogressCalls and update state 
        if (stateCopy.callInfo.inprogressCalls[stateCopy.callInfo]) {

          let inprogressCallsCopy = { ...stateCopy.callInfo.inprogressCalls };
          inprogressCallsCopy[stateCopy.callInfo.activeCallId].audioSettings = audioSettings;
          stateCopy.callInfo.inprogressCalls = inprogressCallsCopy;
        } else {
          console.warn('No inprogress call found', state.callInfo.activeCallId, state.callInfo.inprogressCalls);
        }

        return stateCopy;
      }
    });
  };

  const setIncomingCall = (callData) => {
    return setState((state) => {

      console.log('SETTING SELECTED RING', state, state.settings.selectedRingTone);

      const { callInfo, settings } = state;
      const { activeCallId } = callInfo;

      // Check if activeCall exists to play tone
      if (!activeCallId) {
        playTone(callData.id, settings.selectedRingTone);
      }

      const newState = {
        ...state,
        callInfo: {
          ...state.callInfo,
          incomingCalls: {
            ...callInfo.incomingCalls,
            [callData.id]: {
              callId: callData.id,
              callerName: callData.caller_name ? callData.caller_name : 'caller_name not set',
              callerNumber: callData.caller_id ? callData.caller_id : 'caller_number not set',
              callStatus: callData.status ? callData.status : 'ringing',
              direction: callData.direction ? callData.direction : 'inbound'
            }
          }
        }
      };

      return newState;
    });
  };

  const answerCall = (callId) => {
    return setState((state) => {
      try {
        const { callInfo } = state;
        const { incomingCalls } = callInfo;
        const { callId: id,
          callStatus: status,
          callerNumber: caller_id,
          callerName: caller_name,
        } = incomingCalls[callId];
        const { activeCallId } = callInfo;

        // Removing ringing element for callId if exists.
        removeAudioTag(callId);

        //Holding active call if exists.
        if (typeof activeCallId === "string" && activeCallId.length > 0) {
          callInfo.inprogressCalls[activeCallId].isHeld = true;
          AppService.getInstance().getCallService().hold(activeCallId);
        }

        // update callInfo state.
        const newState = {
          ...state,
          callInfo: {
            ...state.callInfo,
            inprogressCalls: {
              ...callInfo.inprogressCalls,
              [id]: {
                id, status, caller_id, caller_name,
                start_time: Date.now(),
                isMuted: false,
                isHeld: false,
                direction: 'inbound',
                userControlStatus: 'dialing'
              },
            },
            incomingCalls: Object.keys(incomingCalls).filter((incomingCallId) =>
              incomingCallId !== callId).reduce((result, incomingCallId) => ({
                ...result,
                [incomingCallId]: incomingCalls[incomingCallId],
              }), {}),
            activeCallId: callId,
          },
        };
        AppService.getInstance().getCallService().answer(callId);
        return newState;
      } catch (e) {
        console.error('FAILED ON ANSWER CALL', e);

        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });
        return state;
      }
    });
  };

  const mergeCalls = () => {
    return setState((state) => {
      try {
        const { callInfo } = state;
        const [firstCall, secondCall] = Object.keys(callInfo.inprogressCalls);

        const mergedCall = AppService.getInstance().getCallService().threeWayCall(firstCall, secondCall);
        const newState = {
          ...state,
          callInfo: {
            ...state.callInfo,
            inprogressCalls: {
              ...callInfo.inprogressCalls,
              [mergedCall.id]: {
                id: mergedCall.id,
                callId: mergedCall.id,
                callerName: mergedCall.caller_name ? mergedCall.caller_name : 'caller_name not set',
                callerNumber: mergedCall.caller_id ? mergedCall.caller_id : 'caller_number not set',
                callStatus: 'merged',
                direction: 'inbound'
              }
            },
            mergedCalls: callInfo.inprogressCalls
          }
        };

        return newState;
      } catch (e) {
        console.error('Error thrown in mergeCalls', e);
        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });
      }
    });
  };

  const makeCall = async (number) => {
    try {
      // clean the number (remove dashes, convert alpha to numbers)
      const numberToCall = Utilities.cleanPhoneNumber(number);

      // get contact for this number
      let myContact = {
        "items": []
      };
      try {
        myContact = await getContactDataByPhoneNumber(numberToCall);
      } catch (e) {
        console.warn("make call contact name lookup failed",Utilities.formatError(e));
      } 
      const callerName = extractContactNameFromContactArray(numberToCall, myContact.items) || numberToCall;
      const isTransferCall = state.callInfo.transferStatus === 'initiateTransfer';
      const { id, direction } = AppService.getInstance().getCallService().makeCall(numberToCall, isTransferCall);

      return setState((state) => {
        const { callInfo, settings } = state;
        const { activeCallId } = callInfo;
        const newState = {
          ...state,
          callInfo: {
            ...callInfo,
            // remove from incoming calls if you answered it
            incomingCalls: Object.keys(callInfo.incomingCalls).filter((callId) => callId !== id)
              .reduce((result, key) => ({
                ...result,
                [key]: callInfo.incomingCalls[key],
              }), {}),
            activeCallId: id,
            lastNumber: numberToCall,
          },
        };

        // SipJS needs to handle ringback
        playTone(id, settings.selectedRingBack, settings.selectedSpeaker.value);

        //Holding active call if exists.
        if (!!activeCallId && newState.callInfo.transferStatus !== 'initiateTransfer') {
          newState.callInfo.inprogressCalls[activeCallId].isHeld = true;
          AppService.getInstance().getCallService().hold(activeCallId);
        }

        // handle transfer stuff is we are doing transfer.
        if (newState.callInfo.transferStatus === 'initiateTransfer') {
          // set transferStatus for UI.
          newState.callInfo.transferStatus = 'attendedTransfer';
          // set transferDestinationCallId.
          newState.callInfo.transferDestinationCallId = id;
          newState.callInfo.transferCallId = activeCallId;
        }

        // setup inprogressCall object.
        newState.callInfo.inprogressCalls[id] = {
          id,
          isMuted: false,
          isHeld: false,
          status: "dialing",
          caller_id: numberToCall,
          caller_name: callerName,
          direction,
          start_time: Date.now(),
          userControlStatus: "dialing"
        };

        return newState;
      });
    } catch (e) {
      console.error('Error thrown in makeCall', e);
      const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

      createErrorAlert({
        message,
        details: [alertBody],
        autoCloseTimeout: 10,
      });
    }
  };

  const sendDtmf = (callerId, number) => {
    // send number to callerId

    try {
      AppService.getInstance().getCallService().sendDtmf(callerId, number);
    } catch (e) {
      console.error('send dtmf error', e);

      const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

      createErrorAlert({
        message,
        details: [alertBody],
        autoCloseTimeout: 10,
      });
    }
  };

  const blindTransfer = (callerId, numberToTransfer) => {

    try {
      AppService.getInstance().getCallService().blindTransfer(callerId, numberToTransfer);
    } catch (e) {
      console.error('blind transfer error', e);
      const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

      createErrorAlert({
        message,
        details: [alertBody],
        autoCloseTimeout: 10,
      });
    }
    return setState((state) => ({
      ...state,
      callInfo: {
        ...state.callInfo,
        transferStatus: null,
        transferCallId: null,
      }
    }));
  };

  const attendedTransfer = (data) => {
    try {

      console.log('DATA for ATT TRANSFER', data);

      AppService.getInstance().getCallService().attendedTransfer(data.callId, data.toCallId);
    } catch (e) {
      console.error('attended transfer error', e);
      const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

      createErrorAlert({
        message,
        details: [alertBody],
        autoCloseTimeout: 10,
      });
    }
  };

  const holdCall = (id) => {
    return setState((state) => {
      const newState = { ...state };
      const callToHold = newState.callInfo.inprogressCalls[id];
      const isHeld = callToHold ? callToHold.isHeld : null;

      try {
        if (isHeld !== null) {
          if (isHeld) {
            AppService.getInstance().getCallService().unhold(id);
          } else {
            AppService.getInstance().getCallService().hold(id);
          }

          callToHold.isHeld = !isHeld;
        }
      } catch (e) {
        console.error('hold/unhold failed error', e);
        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });

        if (isHeld !== null) {
          // since failure occured, if call exists, set stateCopy back to previous setting.
          callToHold.isHeld = isHeld;
        }
      }
      return newState;
    });
  };

  const sendCallToVoicemail = (id) => {

    return setState((state) => {
      // update callInfo data
      const newState = {
        ...state,
      };
      if (newState.callInfo.incomingCalls[id]) {
        // turn off ringer
        removeAudioTag(id);
      }

      if (newState.callInfo.inprogressCalls[id]) {
        delete newState.callInfo.inprogressCalls[id];
      }

      if (id === newState.callInfo.activeCallId) {
        newState.callInfo.activeCallId = "";
      }

      // what if it was incoming?
      if (newState.callInfo.incomingCalls[id]) {
        delete newState.callInfo.incomingCalls[id];
      }

      // what if it was a transfer call?
      if (id === newState.callInfo.transferCallId) {
        delete newState.callInfo.transferCallId;
        delete newState.callInfo.transferToNbr;
      }

      try {
        const currentExtension = AppService.getInstance().getCallService().getCurrentExtension();

        let vmPrefix = Utilities.getDialPlanOptions(AppService.getInstance().getConnectData().dialplan_options.customerfeaturecodes, "Voicemail Prefix");
        console.log('dialplan options --- ', newState.callInfo);
        console.log('SEND TO VM ------->', vmPrefix + currentExtension.extension, id);

        if (currentExtension.extension.length > 0 && vmPrefix.length > 0) {
          AppService.getInstance().getCallService().blindTransfer(id, vmPrefix + currentExtension.extension);
        } else {

          let errorString = "";
          if (currentExtension.length === 0) {
            errorString = errorString + "Missing currentExtension ";
          }
          if (vmPrefix.length === 0) {
            errorString = errorString + "Missing vmPrefix ";
          }

          console.log("the error is:", errorString);
          const errorObj = Error({ 'statusCode': '1000', 'message': errorString });

          throw (errorObj);
        }

      } catch (e) {
        console.log('Error with sendToVoicemail: ', e);
        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });
      }

      return newState;
    });
  };

  const rejectCall = (id) => {

    return setState((state) => {
      // update callInfo data
      const newState = {
        ...state,
      };
      if (typeof newState.callInfo.incomingCalls[id] !== "undefined") {
        // turn off ringer
        removeAudioTag(id);
      }

      if (newState.callInfo.inprogressCalls[id]) {
        //console.log('2');
        delete newState.callInfo.inprogressCalls[id];
      }

      if (id === newState.callInfo.activeCallId) {
        //console.log('1');
        newState.callInfo.activeCallId = "";
      }

      // what if it was incoming?
      if (newState.callInfo.incomingCalls[id]) {
        delete newState.callInfo.incomingCalls[id];
      }

      // what if it was a transfer call?
      if (id === newState.callInfo.transferCallId) {
        delete newState.callInfo.transferCallId;
        delete newState.callInfo.transferToNbr;
      }

      // TA - there is other logic from old softphone dealing with hungup call
      // that does the following:
      // 1 - if dtmf keypad is showing, toggle to not showing (should this property be in callInfo?)
      // 2 - if this is the last call, fire a state change call state to 'last-call' (need to review this logic)
      // 3 - else if we have no calls (callInfo.inprogressCalls has no data) then change call state to 'no-calls' (need to review this logic)
      //          if setting update is pending (no settings update while calls in progress), then proceed with settings update
      //          route to /appmain
      // 4 - else we still have inprogress calls, so if not on route /callprogress, go there

      // Need to see how this all fits with callprogress route

      try {
        setTimeout(() => { AppService.getInstance().getCallService().reject(id); }, 500);
      } catch (e) {
        console.log('Reject error...could have gotten this error because the other end terminated: ', e);

        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));
        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });
      }

      return newState;
    });
  };

  const hangupCall = (id) => {
    // console.warn('####### HANGUP CALL', id);
    return setState((state) => {
      const newState = {
        ...state,
      };
      // turn off ringer / ringback if it exists
      removeAudioTag(id);

      // remove call from inprogressCalls
      if (newState.callInfo.inprogressCalls[id]) {
        //console.log('2');
        delete newState.callInfo.inprogressCalls[id];
      }

      // if it was activeCall, reset activeCallId to undefined if there are no other inprogressCalls, 
      // otherwise set the id for inprogressCalls[0]
      if (id === newState.callInfo.activeCallId) {
        //console.log('1');
        newState.callInfo.activeCallId = Object.keys(newState.callInfo.inprogressCalls).length > 0 ?
          Object.keys(newState.callInfo.inprogressCalls)[0] : "";
      }

      // what if it was merged? Remove call from mergedCalls
      if (Object.keys(newState.callInfo.inprogressCalls).length === 0 && Object.keys(newState.callInfo.mergedCalls).length > 0) {
        Object.keys(newState.callInfo.mergedCalls).forEach(key => {
          delete newState.callInfo.mergedCalls[key];
        });
      }

      // what if it was incoming? Remove call from incomingCalls
      if (typeof newState.callInfo.incomingCalls[id] !== "undefined") {
        // console.log('>>>>>>>>>>> incoming call so removing it ', stateCopy)
        delete newState.callInfo.incomingCalls[id];
      }

      // what if it was a transfer call? Cancel the transfer
      if (id === newState.callInfo.transferCallId) {
        // clear the transfer data 
        delete newState.callInfo.transferCallId;
        delete newState.callInfo.transferStatus;
        delete newState.callInfo.transferDestinationCallId;
      }

      // what if it was transferDestination?
      if (id === newState.callInfo.transferDestinationCallId) {
        // clear the transferDestinationCallId
        delete newState.callInfo.transferDestinationCallId;
        delete newState.callInfo.transferStatus;

        // Make the transferCallId the active call if activeCallId is undefined
        if (newState.callInfo.activeCallId.length === 0) {
          newState.callInfo.activeCallId = newState.callInfo.transferCallId;
          // 
          delete newState.callInfo.transferCallId;
        }
      }

      // TA - there is other logic from old softphone dealing with hungup call
      // that does the following:
      // 1 - if dtmf keypad is showing, toggle to not showing (should this property be in callInfo?)
      // 2 - if this is the last call, fire a state change call state to 'last-call' (need to review this logic)
      // 3 - else if we have no calls (callInfo.inprogressCalls has no data) then change call state to 'no-calls' (need to review this logic)
      //          if setting update is pending (no settings update while calls in progress), then proceed with settings update
      //          route to /appmain
      // 4 - else we still have inprogress calls, so if not on route /callprogress, go there

      // Need to see how this all fits with callprogress route
      try {
        AppService.getInstance().getCallService().hangup(id);
      } catch (e) {
        console.log('Hangup failed ... could have gotten this error because the other end terminated: ', e);
        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });
      }
      return newState;
    });
  };

  const callCleanup = (id) => {
    return setState((state) => {
      // update callInfo data
      const newState = {
        ...state,
      };
      // turn off ringer / ringback if it exists
      removeAudioTag(id);

      // remove call from inprogressCalls
      if (newState.callInfo.inprogressCalls[id]) {
        //console.log('2');
        delete newState.callInfo.inprogressCalls[id];
      }

      // if it was activeCall, reset activeCallId to undefined if there are no other inprogressCalls, 
      // otherwise set the id for inprogressCalls[0]
      if (id === newState.callInfo.activeCallId) {
        //console.log('1');
        newState.callInfo.activeCallId = Object.keys(newState.callInfo.inprogressCalls).length > 0 ?
          Object.keys(newState.callInfo.inprogressCalls)[0] : "";
      }


      // what if it was incoming? Remove call from incomingCalls
      if (typeof newState.callInfo.incomingCalls[id] !== "undefined") {
        console.log('>>>>>>>>>>> incoming call so removing it ', newState);
        delete newState.callInfo.incomingCalls[id];
      }

      // what if it was a transfer call? Cancel the transfer
      if (id === newState.callInfo.transferCallId) {
        // clear the transfer data 
        delete newState.callInfo.transferCallId;
        delete newState.callInfo.transferStatus;
        delete newState.callInfo.transferDestinationCallId;
      }

      // what if it was transferDestination?
      if (id === newState.callInfo.transferDestinationCallId) {
        // clear the transferDestinationCallId
        delete newState.callInfo.transferDestinationCallId;
        delete newState.callInfo.transferStatus;

        // Make the transferCallId the active call if activeCallId is undefined
        if (newState.callInfo.activeCallId.length === 0) {
          newState.callInfo.activeCallId = newState.callInfo.transferCallId;
          // 
          delete newState.callInfo.transferCallId;
        }
      }

      return newState;
    });
  };

  const muteCall = (id) => {
    return setState((state) => {
      // update callInfo data
      const newState = {
        ...state,
        callInfo: {
          ...state.callInfo,
        },
      };

      // check if there any inprogressCalls before trying to mute/unmute.  Found that if user has headset with mute button, this call 
      // can be made when there are no active calls
      if (newState.callInfo.inprogressCalls && newState.callInfo.inprogressCalls[id]) {
        const origValue = newState.callInfo.inprogressCalls[id].isMuted;
        try {
          newState.callInfo.inprogressCalls[id].isMuted = !origValue;
          if (origValue) {
            AppService.getInstance().getCallService().unmute(id);
          } else {
            AppService.getInstance().getCallService().mute(id);
          }
        } catch (e) {
          console.error('mute/unmute failed error', e);

          const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

          createErrorAlert({
            message,
            details: [alertBody],
            autoCloseTimeout: 10,
          });

          // since failure occured, set stateCopy back to previous setting
          newState.callInfo.inprogressCalls[id].isHeld = origValue;
        }
      }
      return newState;
    });
  };

  const handleDialPlanOptions = (dialPlanOptions) => {
    return setState((state) => {
      return {
        ...state,
        callInfo: {
          ...state.callInfo,
          dialPlanOptions: dialPlanOptions,
        },
      };
    });
  };

  const handleUpdateVoicemail = async (vmData) => {
    return setState((state) => {
      return {
        ...state,
        callInfo: {
          ...state.callInfo,
          voicemailCount: vmData.unread_count,
        }
      };
    });
  };

  const updateCallStatus = (callData) => {

    return setState((state) => {
      const newState = {
        ...state,
      };

      if (typeof newState.callInfo.inprogressCalls[callData.id] !== "undefined") {
        if (typeof callData.status !== "undefined") {
          newState.callInfo.inprogressCalls[callData.id].status = callData.status;
        }

        if (typeof callData.userControlStatus !== "undefined") {
          newState.callInfo.inprogressCalls[callData.id].userControlStatus = callData.userControlStatus;
        }
      }
      return newState;
    });
  };

  const connectedCall = (callData) => {
    return setState((state) => {
      // remove ringer or ringback if it exsist
      removeAudioTag(callData.id);

      // Building an object shape that CallService expects
      const callServiceSettings = {
        outputDevice: {
          id: state.settings.selectedSpeaker.value,
        },
        outputVolume: state.settings.outputVolume
      };

      try {
        // calling setAudio to set selected speaker and volume
        AppService.getInstance().getCallService().setAudio(callData.id, callServiceSettings);

      } catch (e) {
        console.error('Error setting audio in CallInfo -- connectedCall');
        const { alertHeading: message, alertBody } = processServiceErrorsToAlerts(Utilities.formatError(e));

        createErrorAlert({
          message,
          details: [alertBody],
          autoCloseTimeout: 10,
        });

      }
      return {
        ...state,
      };
    });
  };

  const initiateTransfer = (transferCallId) => {
    return setState((state) => ({
      ...state,
      callInfo: {
        ...state.callInfo,
        transferStatus: 'initiateTransfer',
        transferCallId,
      },
    }));
  };

  const cancelTransfer = () => {
    return setState((state) => ({
      ...state,
      callInfo: {
        ...state.callInfo,
        transferStatus: null,
        transferCallId: null,
      },
    }));
  };

  const cReturn = state !== undefined && state.callInfo ? state.callInfo : initialState;

  return [cReturn, {
    setActiveCallId,
    saveAudioSettings,
    setIncomingCall,
    makeCall,
    answerCall,
    holdCall,
    hangupCall,
    sendCallToVoicemail,
    rejectCall,
    muteCall,
    sendDtmf,
    blindTransfer,
    attendedTransfer,
    handleDialPlanOptions,
    handleUpdateVoicemail,
    updateCallStatus,
    initiateTransfer,
    cancelTransfer,
    connectedCall,
    callCleanup,
    mergeCalls
  }];
};

export default useCallInfo;

export { initialState };